import { useEffect, useState } from "react";
import { Share2, Copy, MessageCircle, Mail, Trash2, Loader2, CheckCircle2, Link as LinkIcon } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import MobileModal from "@/components/MobileModal";

export default function ShareTrackingModal({ orderId, open, onClose, readOnly = false }) {
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);

  const fetchExisting = async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/share-token`);
      if (!data.revoked) setToken(data);
    } catch { /* 404 = none yet */ }
  };

  useEffect(() => {
    if (open) fetchExisting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orderId]);

  const createToken = async () => {
    if (readOnly) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/orders/${orderId}/share-token`);
      setToken(data);
      toast.success("Link di tracking generato.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const revoke = async () => {
    if (readOnly) return;
    if (!window.confirm("Revocare il link di tracking? Non sarà più accessibile.")) return;
    setLoading(true);
    try {
      await api.delete(`/orders/${orderId}/share-token`);
      setToken(null);
      toast.success("Link revocato.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const copyLink = () => {
    if (!token?.tracking_url) return;
    navigator.clipboard.writeText(token.tracking_url).then(() => toast.success("Link copiato negli appunti"));
  };

  // Phase 44.29 — Messaggio precompilato fornito dal backend (include marca,
  // modello, colore e targa del veicolo se il driver è già assegnato).
  // Fallback al testo statico se il backend non lo invia (es. retro-compat).
  const shareMessage = token?.share_message
    || "Ciao, la tua consegna DapNow è in corso. Puoi seguire lo stato aggiornato qui:";
  const whatsappUrl = token ? `https://wa.me/?text=${encodeURIComponent(`${shareMessage}\n${token.tracking_url}`)}` : "#";
  const emailUrl = token ? `mailto:?subject=${encodeURIComponent("La tua consegna DapNow")}&body=${encodeURIComponent(`${shareMessage}\n\n${token.tracking_url}`)}` : "#";

  if (!open) return null;
  return (
    <MobileModal
      open={open}
      onClose={onClose}
      title="Invia il link al destinatario"
      description="Condividi tracking"
      testid="share-tracking-modal"
      size="md"
    >
        {!token ? (
          <div className="text-center py-6">
            <LinkIcon className="size-10 mx-auto text-dapnow-muted mb-3" />
            {readOnly ? (
              <p className="text-sm text-dapnow-muted mb-5 max-w-xs mx-auto" data-testid="share-readonly-empty">
                Il link di tracking non è ancora disponibile o è stato revocato dal cliente. Riprova più tardi.
              </p>
            ) : (
              <p className="text-sm text-dapnow-muted mb-5 max-w-xs mx-auto">
                Genera un link pubblico sicuro che il destinatario può aprire senza login per vedere ETA, mappa e stato consegna.
                Condividilo solo con le persone coinvolte nella consegna.
              </p>
            )}
            {!readOnly && (
              <button
                onClick={createToken}
                disabled={loading}
                data-testid="generate-share-token-btn"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-dapnow-blue text-white font-semibold hover:bg-dapnow-blue/90 transition"
              >
                {loading ? <Loader2 className="size-4 animate-spin" /> : <LinkIcon className="size-4" />}
                Genera link condivisibile
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
              <CheckCircle2 className="size-4" /> Link attivo — scade il {token.expires_at ? new Date(token.expires_at).toLocaleDateString("it-CH") : "—"}
            </div>

            <div className="rounded-xl bg-zinc-100 border border-zinc-200 p-3 flex items-center gap-2" data-testid="share-token-url">
              <p className="flex-1 text-xs font-mono text-dapnow-ink break-all">{token.tracking_url}</p>
              <button
                onClick={copyLink}
                data-testid="copy-share-link-btn"
                className="inline-flex items-center gap-1 text-xs font-semibold text-dapnow-blue hover:underline shrink-0"
              >
                <Copy className="size-3" /> Copia
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-whatsapp-btn"
                className="h-12 inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition"
              >
                <MessageCircle className="size-4" /> WhatsApp
              </a>
              <a
                href={emailUrl}
                data-testid="share-email-btn"
                className="h-12 inline-flex items-center justify-center gap-2 rounded-xl bg-zinc-200 text-dapnow-ink font-semibold hover:bg-zinc-300 transition"
              >
                <Mail className="size-4" /> Email
              </a>
            </div>

            <p className="text-[11px] text-dapnow-muted leading-relaxed">
              Il destinatario vede: stato, ETA, posizione driver live, nome parziale del trasportatore e punti di ritiro/consegna.
              <strong> Non</strong> vede: telefoni, email, dettagli di pagamento o dati aziendali.
            </p>

            <button
              onClick={revoke}
              disabled={loading}
              data-testid="revoke-share-token-btn"
              className={`w-full inline-flex items-center justify-center gap-2 h-11 text-sm font-semibold text-dapnow-red hover:bg-dapnow-red/5 rounded-xl ${readOnly ? "hidden" : ""}`}
            >
              {loading ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
              Revoca link
            </button>
          </div>
        )}
      <span className="hidden">
        <Share2 className="size-3" />
      </span>
    </MobileModal>
  );
}
